import React from 'react';

interface SparklineProps {
  data: number[];
  width?: number;
  height?: number;
  color?: string;
  fillColor?: string;
}

const Sparkline: React.FC<SparklineProps> = ({
  data,
  width = 100,
  height = 24,
  color = '#4a90d9',
  fillColor = 'rgba(74, 144, 217, 0.15)',
}) => {
  if (!data || data.length === 0) {
    return (
      <div style={{ width, height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ fontSize: '10px', color: '#999' }}>No data</span>
      </div>
    );
  }

  const max = Math.max(...data, 1); // Ensure at least 1 to avoid division by zero
  const min = 0;
  const range = max - min;

  const padding = 2;
  const chartWidth = width - padding * 2;
  const chartHeight = height - padding * 2;

  // Generate points for the line
  const points = data.map((value, index) => {
    const x = padding + (index / (data.length - 1 || 1)) * chartWidth;
    const y = padding + chartHeight - ((value - min) / (range || 1)) * chartHeight;
    return `${x},${y}`;
  });

  const linePath = `M ${points.join(' L ')}`;

  // Create closed path for fill (add bottom corners)
  const fillPath = `${linePath} L ${padding + chartWidth},${padding + chartHeight} L ${padding},${padding + chartHeight} Z`;

  return (
    <svg
      width={width}
      height={height}
      style={{ display: 'block' }}
      viewBox={`0 0 ${width} ${height}`}
    >
      {/* Fill area under the line */}
      <path
        d={fillPath}
        fill={fillColor}
        stroke="none"
      />
      {/* The line itself */}
      <path
        d={linePath}
        fill="none"
        stroke={color}
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* End point dot */}
      {data.length > 0 && (
        <circle
          cx={padding + chartWidth}
          cy={padding + chartHeight - ((data[data.length - 1] - min) / (range || 1)) * chartHeight}
          r={2}
          fill={color}
        />
      )}
    </svg>
  );
};

export default Sparkline;
